import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Apple, Plug } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { AppleCareBadge, AsmBadge, CheckInText, MdmBadge } from '../components/device-badges';
import { AssetStatusBadge } from '../components/badges';
import { Button, Card, cn, EmptyState, ErrorState, Input, PageHeader, Pagination, Select, Spinner, Table, Td, Th } from '../components/ui';
import { CampusBadge } from '../components/work-order-badges';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { useDebouncedValue, useUrlState } from '../lib/hooks';
import { DEVICE_FAMILY_LABEL } from '../lib/integrations';
import type { ManagedDeviceFilters, ManagedDeviceListItem, DeviceFamily, Page } from '../lib/types';

const PAGE_SIZE = 25;
const DEFAULTS = {
  search: '',
  family: '',
  mdm: '',
  asm: '',
  notCheckedInDays: '',
  appleCare: '',
  model: '',
  site: '',
  linked: '',
  page: '1',
  sort: 'lastCheckInAt',
  dir: 'desc',
};

export function ManagedDevicesPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(filters.search);
  const search = useDebouncedValue(searchInput, 300);
  const page = Number(filters.page) || 1;

  useEffect(() => {
    if (search !== filters.search) setFilters({ search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const options = useQuery({ queryKey: ['managed-devices', 'filters'], queryFn: () => api.get<ManagedDeviceFilters>('/managed-devices/filters') });
  const devices = useQuery({
    queryKey: ['managed-devices', 'list', filters],
    placeholderData: keepPreviousData,
    queryFn: () => api.get<Page<ManagedDeviceListItem>>('/managed-devices', { ...filters, page, pageSize: PAGE_SIZE }),
  });

  const sortBy = (field: string) => setFilters({ sort: field, dir: filters.sort === field && filters.dir === 'desc' ? 'asc' : 'desc' });
  const sortable = (label: string, field: string) => (
    <button onClick={() => sortBy(field)} className={filters.sort === field ? 'text-slate-800 uppercase' : 'uppercase'}>
      {label}
      {filters.sort === field && (filters.dir === 'asc' ? ' ↑' : ' ↓')}
    </button>
  );

  const sources = options.data?.sources;
  const quickFilters = [
    { label: 'Jamf Pro', count: sources?.JAMF_PRO, active: filters.mdm === 'JAMF_PRO', apply: { mdm: filters.mdm === 'JAMF_PRO' ? '' : 'JAMF_PRO' } },
    { label: 'Jamf School', count: sources?.JAMF_SCHOOL, active: filters.mdm === 'JAMF_SCHOOL', apply: { mdm: filters.mdm === 'JAMF_SCHOOL' ? '' : 'JAMF_SCHOOL' } },
    { label: 'Mosyle', count: sources?.MOSYLE, active: filters.mdm === 'MOSYLE', apply: { mdm: filters.mdm === 'MOSYLE' ? '' : 'MOSYLE' } },
    { label: 'Intune', count: sources?.INTUNE, active: filters.mdm === 'INTUNE', apply: { mdm: filters.mdm === 'INTUNE' ? '' : 'INTUNE' } },
    { label: 'Apple School Manager', count: sources?.APPLE_SCHOOL_MANAGER, active: filters.asm === 'any' && !filters.mdm, apply: { asm: filters.asm === 'any' ? '' : 'any', mdm: '' } },
    {
      label: 'Purchased, not enrolled',
      count: options.data?.notEnrolled,
      active: filters.asm === 'any' && filters.mdm === 'none',
      apply: filters.asm === 'any' && filters.mdm === 'none' ? { asm: '', mdm: '' } : { asm: 'any', mdm: 'none' },
    },
    {
      label: 'No check-in for 30+ days',
      count: options.data?.notCheckedIn30Days,
      active: filters.notCheckedInDays === '30',
      apply: { notCheckedInDays: filters.notCheckedInDays === '30' ? '' : '30' },
    },
  ].filter((chip) => chip.count);

  return (
    <>
      <PageHeader
        title="Managed devices"
        description="Macs, iPads, Apple TVs, and Windows PCs from Jamf, Mosyle, Intune, and Apple School Manager, matched by serial number."
        actions={
          can('TECHNICIAN') && (
            <Link to="/integrations">
              <Button icon={<Plug />}>Manage connections</Button>
            </Link>
          )
        }
      />

      {quickFilters.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {quickFilters.map((chip) => (
            <button
              key={chip.label}
              onClick={() => setFilters(chip.apply)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                chip.active ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300',
              )}
            >
              {chip.label} <span className="font-semibold">{chip.count!.toLocaleString()}</span>
            </button>
          ))}
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search serial, name, user, asset tag, order" className="w-full sm:w-72" />
          <Select value={filters.family} onChange={(e) => setFilters({ family: e.target.value })} className="w-auto" aria-label="Device type">
            <option value="">All device types</option>
            {options.data?.families.map((f) => (
              <option key={f.value} value={f.value}>
                {DEVICE_FAMILY_LABEL[f.value as DeviceFamily]} ({f.count})
              </option>
            ))}
          </Select>
          <Select value={filters.mdm} onChange={(e) => setFilters({ mdm: e.target.value })} className="w-auto" aria-label="MDM">
            <option value="">Enrolled or not</option>
            <option value="any">Enrolled in an MDM</option>
            <option value="JAMF_PRO">Jamf Pro</option>
            <option value="JAMF_SCHOOL">Jamf School</option>
            <option value="MOSYLE">Mosyle</option>
            <option value="INTUNE">Microsoft Intune</option>
            <option value="none">Not enrolled</option>
          </Select>
          <Select value={filters.asm} onChange={(e) => setFilters({ asm: e.target.value })} className="w-auto" aria-label="Apple School Manager">
            <option value="">Any Apple School Manager status</option>
            <option value="any">Listed in Apple School Manager</option>
            <option value="ASSIGNED">Assigned to an MDM server</option>
            <option value="UNASSIGNED">Not assigned to an MDM server</option>
            <option value="none">Not listed</option>
            <option value="released">Released</option>
          </Select>
          <Select value={filters.notCheckedInDays} onChange={(e) => setFilters({ notCheckedInDays: e.target.value })} className="w-auto" aria-label="Last check-in">
            <option value="">Any last check-in</option>
            <option value="7">No check-in for 7+ days</option>
            <option value="30">No check-in for 30+ days</option>
            <option value="90">No check-in for 90+ days</option>
          </Select>
          <Select value={filters.appleCare} onChange={(e) => setFilters({ appleCare: e.target.value })} className="w-auto" aria-label="Coverage">
            <option value="">Any coverage</option>
            <option value="active">Covered</option>
            <option value="within_90">Coverage ends within 90 days</option>
            <option value="expired">Coverage ended</option>
          </Select>
          {!!options.data?.models.length && (
            <Select value={filters.model} onChange={(e) => setFilters({ model: e.target.value })} className="w-auto max-w-[14rem]" aria-label="Model">
              <option value="">All models</option>
              {options.data.models.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.value} ({m.count})
                </option>
              ))}
            </Select>
          )}
          {!!options.data?.sites.length && (
            <Select value={filters.site} onChange={(e) => setFilters({ site: e.target.value })} className="w-auto" aria-label="Site">
              <option value="">All sites</option>
              {options.data.sites.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.value} ({s.count})
                </option>
              ))}
            </Select>
          )}
          <Select value={filters.linked} onChange={(e) => setFilters({ linked: e.target.value })} className="w-auto" aria-label="Asset link">
            <option value="">Linked or not</option>
            <option value="true">Linked to an asset</option>
            <option value="false">Not linked</option>
          </Select>
          {devices.isFetching && !devices.isLoading && <Spinner className="size-4" />}
        </div>

        {devices.isLoading ? (
          <div className="flex h-60 items-center justify-center">
            <Spinner />
          </div>
        ) : devices.error ? (
          <ErrorState error={devices.error} onRetry={() => devices.refetch()} />
        ) : devices.data!.items.length === 0 ? (
          <EmptyState
            icon={<Apple />}
            title="No managed devices found"
            description="Adjust the filters, or connect Jamf, Mosyle, Intune, or Apple School Manager under Integrations."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{sortable('Serial / model', 'serialNumber')}</Th>
                  <Th>{sortable('Name / user', 'assignedUserName')}</Th>
                  <Th>MDM</Th>
                  <Th>{sortable('Last check-in', 'lastCheckInAt')}</Th>
                  <Th>Apple School Manager</Th>
                  <Th>{sortable('Coverage', 'appleCareEndsAt')}</Th>
                  <Th>Asset</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {devices.data!.items.map((device) => (
                  <tr key={device.id} className="cursor-pointer hover:bg-slate-50/80" onClick={() => navigate(`/managed-devices/${device.id}`)}>
                    <Td className="max-w-[15rem]">
                      <Link to={`/managed-devices/${device.id}`} onClick={(e) => e.stopPropagation()} className="font-mono text-xs font-semibold text-brand-800 hover:underline">
                        {device.serialNumber}
                      </Link>
                      <p className="truncate text-xs text-slate-500">{device.model ?? DEVICE_FAMILY_LABEL[device.productFamily]}</p>
                    </Td>
                    <Td className="max-w-[14rem]">
                      <p className="truncate text-slate-800">{device.name ?? <span className="text-slate-400">—</span>}</p>
                      {(device.assignedUserName || device.assignedUserEmail) && (
                        <p className="truncate text-xs text-slate-500">{device.assignedUserName ?? device.assignedUserEmail}</p>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">
                      <MdmBadge device={device} />
                      {device.osVersion && device.mdmProvider && !device.mdmRemovedAt && (
                        <p className="mt-0.5 text-xs text-slate-500">
                          {device.osName} {device.osVersion}
                        </p>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">
                      {device.mdmProvider ? (
                        <span title={formatDateTime(device.lastCheckInAt)}>
                          <CheckInText date={device.lastCheckInAt} />
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                      {device.campus && device.campus !== 'UNKNOWN' && (
                        <div className="mt-1">
                          <CampusBadge location={device.campus} />
                        </div>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap">
                      <AsmBadge device={device} />
                    </Td>
                    <Td className="whitespace-nowrap">
                      <AppleCareBadge endsAt={device.appleCareEndsAt} description={device.appleCareDescription} />
                    </Td>
                    <Td className="whitespace-nowrap">
                      {device.asset ? (
                        <Link to={`/assets/${device.asset.id}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-2">
                          <span className="font-mono text-xs font-semibold text-brand-800 hover:underline">{device.asset.assetTag}</span>
                          <AssetStatusBadge status={device.asset.status} />
                        </Link>
                      ) : (
                        <span className="text-xs text-slate-400">Not linked</span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={PAGE_SIZE} total={devices.data!.total} onPage={(p) => setFilters({ page: String(p) }, { resetPage: false })} />
          </>
        )}
      </Card>
    </>
  );
}
